import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MagnifyingGlass, ArrowClockwise, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const ACTION_LABELS = {
  "user.created": "User created",
  "user.deactivated": "User deactivated",
  "user.reactivated": "User reactivated",
  "user.role_changed": "Role changed",
  "return.deleted": "Return deleted",
  "notice.deleted": "Notice deleted",
  "payment.deleted": "Payment deleted",
  "task.deleted": "Task deleted",
};

const ACTION_STYLES = {
  "user.created": "bg-emerald-50 text-emerald-700",
  "user.deactivated": "bg-[#D32F2F]/10 text-[#D32F2F]",
  "user.reactivated": "bg-blue-50 text-blue-700",
  "user.role_changed": "bg-amber-50 text-amber-700",
  "return.deleted": "bg-[#D32F2F]/10 text-[#D32F2F]",
  "notice.deleted": "bg-[#D32F2F]/10 text-[#D32F2F]",
  "payment.deleted": "bg-[#D32F2F]/10 text-[#D32F2F]",
  "task.deleted": "bg-[#D32F2F]/10 text-[#D32F2F]",
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [actionFilter, setActionFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = actionFilter && actionFilter !== "all" ? `?action=${encodeURIComponent(actionFilter)}` : "";
      const { data } = await api.get(`/audit-logs${params}`);
      setEntries(data);
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((e) =>
      (e.actor_email || "").toLowerCase().includes(needle) ||
      (e.actor_name || "").toLowerCase().includes(needle) ||
      (e.target_name || "").toLowerCase().includes(needle) ||
      JSON.stringify(e.metadata || {}).toLowerCase().includes(needle),
    );
  }, [entries, q]);

  const formatMeta = (e) => {
    const m = e.metadata || {};
    if (e.action === "user.role_changed") return `${m.old_role} → ${m.new_role}`;
    if (m.role) return `role: ${m.role}`;
    if (m.amount !== undefined) return `$${Number(m.amount).toLocaleString()}`;
    if (m.notice_type) return m.notice_type;
    if (m.stage) return `${m.stage} · ${m.priority || ""}`;
    return m.email || m.owner_name || "";
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/audit-logs/${toDelete.id}`);
      toast.success("Audit entry deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="audit-log-title">Audit Log</h1>
          <p className="text-slate-600 mt-1">Every admin action on the platform — searchable, filterable and purgeable.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="audit-refresh">
          <ArrowClockwise size={14} className="mr-1" /> {loading ? "Loading…" : "Refresh"}
        </Button>
      </div>

      <div className="rounded-md border tf-hairline bg-white p-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by actor, target or email…" className="pl-9" data-testid="audit-search" />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-56" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="user.created">User created</SelectItem>
            <SelectItem value="user.deactivated">User deactivated</SelectItem>
            <SelectItem value="user.reactivated">User reactivated</SelectItem>
            <SelectItem value="user.role_changed">Role changed</SelectItem>
            <SelectItem value="return.deleted">Return deleted</SelectItem>
            <SelectItem value="notice.deleted">Notice deleted</SelectItem>
            <SelectItem value="payment.deleted">Payment deleted</SelectItem>
            <SelectItem value="task.deleted">Task deleted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border tf-hairline bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-3">When</th>
              <th className="text-left p-3">Actor</th>
              <th className="text-left p-3">Action</th>
              <th className="text-left p-3">Target</th>
              <th className="text-left p-3">Detail</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500" data-testid="audit-empty">No audit entries match this filter.</td></tr>
            )}
            {filtered.map((e) => (
              <tr key={e.id} className="border-t tf-hairline hover:bg-slate-50">
                <td className="p-3 font-mono text-xs whitespace-nowrap">{(e.created_at || "").replace("T", " ").slice(0, 19)}</td>
                <td className="p-3">
                  <div className="font-semibold text-[#0A2540]">{e.actor_name}</div>
                  <div className="text-xs text-slate-500">{e.actor_email}</div>
                </td>
                <td className="p-3">
                  <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded ${ACTION_STYLES[e.action] || "bg-slate-100 text-slate-700"}`}>
                    {ACTION_LABELS[e.action] || e.action}
                  </span>
                </td>
                <td className="p-3">
                  <div className="font-semibold text-[#0A2540]">{e.target_name}</div>
                  <div className="text-xs text-slate-500">{(e.metadata || {}).email}</div>
                </td>
                <td className="p-3 text-slate-600 font-mono text-xs">{formatMeta(e)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setToDelete(e)} data-testid={`delete-audit-${e.id}`}>
                    <Trash size={14} className="text-[#D32F2F]" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete audit entry?</AlertDialogTitle>
            <AlertDialogDescription>
              Audit trails are typically preserved for compliance. Only delete an entry if it was recorded in error. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-audit-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="delete-audit-confirm">
              Delete entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
